import Immutable from 'immutable';
import * as actions from './actions';
import { STATUS, SAVING_STATUS } from '../constants';

export const reducerKeys = {
  exams: 'exams',
  examLoadingStatus: 'examLoadingStatus',
  examLoadingErrors: 'examLoadingErrors',
  examSections: 'examSections',
  examQuestions: 'examQuestions',
  sittingsByExamId: 'sittingsByExamId',
  responsesByQuestionId: 'responsesByQuestionId',
  solutionsByQuestionId: 'solutionsByQuestionId',
  classQuestionResponses: 'classQuestionResponses',
  classQuestionSetAggregates: 'classQuestionSetAggregates',
  classExamSittings: 'classExamSittings',
  classExamSittingsStatus: 'classExamSittingsStatus',
  loadingText: 'loadingText',
  checklistItemSavingByQuestionId: 'checklistItemSavingByQuestionId',
  grokkingItemSavingByQuestionId: 'grokkingItemSavingByQuestionId',
  markingItemSavingByQuestionId: 'markingItemSavingByQuestionId',
};

const defaultState = Immutable.fromJS({
  [reducerKeys.exams]: {},
  [reducerKeys.examLoadingStatus]: {},
  [reducerKeys.examLoadingErrors]: {},
  [reducerKeys.examSections]: {},
  [reducerKeys.examQuestions]: {},
  [reducerKeys.sittingsByExamId]: {},
  [reducerKeys.responsesByQuestionId]: {},
  [reducerKeys.solutionsByQuestionId]: {},
  [reducerKeys.classQuestionResponses]: {},

  // structure: .getIn([examId, classId]) -> classExamData
  [reducerKeys.classQuestionSetAggregates]: {},
  [reducerKeys.classExamSittings]: {},
  [reducerKeys.classExamSittingsStatus]: {},

  [reducerKeys.loadingText]: null,
  [reducerKeys.checklistItemSavingByQuestionId]: {},
  [reducerKeys.grokkingItemSavingByQuestionId]: {},
  [reducerKeys.markingItemSavingByQuestionId]: {},
});

export const pathGenerators = {
  classQuestionResponses: (classId, examId, questionId) => [
    reducerKeys.classQuestionResponses,
    `C:${classId}|E:${examId}|Q:${questionId}`,
  ],
  responsesByQuestionId: (questionId) => [
    reducerKeys.responsesByQuestionId,
    `${questionId}`,
  ],
  grokkingItemSavingByQuestionId: (questionId) => [
    reducerKeys.grokkingItemSavingByQuestionId,
    `${questionId}`,
  ],
  markingItemSavingByQuestionId: (questionId) => [
    reducerKeys.markingItemSavingByQuestionId,
    `${questionId}`,
  ],
  checklistItemSavingByQuestionId: (questionId) => [
    reducerKeys.checklistItemSavingByQuestionId,
    `${questionId}`,
  ],
  checklistReflectionsByQuestionId: (questionId) => [
    reducerKeys.responsesByQuestionId,
    `${questionId}`,
    'checklist_reflections',
  ],
};

// Main Reducer export
export default (state = defaultState, action) => {
  let path;
  switch (action.type) {
    case actions.LOAD_EXAM_REQUEST:
      return state.setIn(
        [reducerKeys.examLoadingStatus, `${action.payload.examId}`],
        STATUS.LOADING
      );

    case actions.LOAD_EXAM_SUCCESS:
      return state
        .mergeDeep(action.payload.examData.entities)
        .setIn(
          [reducerKeys.examLoadingStatus, `${action.payload.examId}`],
          STATUS.READY
        );

    case actions.LOAD_EXAM_FAILURE:
      return state
        .setIn(
          [reducerKeys.examLoadingStatus, `${action.payload.examId}`],
          STATUS.FAILURE
        )
        .setIn(
          [reducerKeys.examLoadingErrors, `${action.payload.examId}`],
          Immutable.fromJS({
            status: action.payload.status,
            failureReason: action.payload.failureReason,
          })
        );

    case actions.LOAD_EXAM_SOLUTION_REQUEST:
      return state.set('loadingText', 'Loading Exam Solution');

    case actions.LOAD_EXAM_SOLUTION_SUCCESS:
      return state
        .mergeDeep(action.payload.solutionData.entities)
        .set('loadingText', null);

    case actions.LOAD_EXAM_SOLUTION_FAILURE:
      return state.set('loadingText', null);
    // TODO: something with error messages

    case actions.LOAD_SITTING_REQUEST:
      return state.set(reducerKeys.loadingText, 'Loading Responses');

    case actions.LOAD_CLASS_QUESTION_RESPONSES_REQUEST:
      return state.set('loadingText', 'Loading Class Responses');

    case actions.LOAD_CLASS_QUESTION_RESPONSES_SUCCESS: {
      const { classId, examId, questionId, responses } = action.payload;
      path = pathGenerators.classQuestionResponses(classId, examId, questionId);
      return state
        .setIn(path, Immutable.fromJS(responses))
        .set('loadingText', null);
    }

    case actions.LOAD_CLASS_QUESTION_RESPONSES_FAILURE:
      return state.set('loadingText', null);
    // TODO: something with error messages

    case actions.CREATE_SITTING_REQUEST:
      return state.set(reducerKeys.loadingText, 'Creating Sitting');

    // both load and create return the sitting
    case actions.LOAD_SITTING_SUCCESS:
    case actions.CREATE_SITTING_SUCCESS:
      return state
        .set(reducerKeys.loadingText, null)
        .mergeDeep(action.payload.sittingData.entities);

    case actions.CREATE_SITTING_FAILURE:
    case actions.LOAD_SITTING_FAILURE:
      return state.set(reducerKeys.loadingText, null);
    // TODO: be more better with error messages

    case actions.SUBMIT_QUESTION_RESPONSE_SUCCESS:
      return state.mergeDeep(action.payload.responseData.entities);

    case actions.SUBMIT_QUESTION_GROK_REQUEST:
      path = [
        ...pathGenerators.responsesByQuestionId(action.payload.questionId),
        'grokked',
      ];
      return state
        .setIn(path, action.payload.grokked)
        .setIn(
          pathGenerators.grokkingItemSavingByQuestionId(
            action.payload.questionId
          ),
          SAVING_STATUS.SAVING
        );

    case actions.SUBMIT_QUESTION_GROK_SUCCESS:
      return (
        state
          .setIn(
            pathGenerators.grokkingItemSavingByQuestionId(
              action.payload.questionId
            ),
            SAVING_STATUS.SAVED
          )
          // .console.log(action.payload.responseData.entities)
          .mergeDeep(action.payload.responseData.entities)
      );

    case actions.SUBMIT_QUESTION_GROK_FAILURE:
      path = [
        ...pathGenerators.responsesByQuestionId(action.payload.questionId),
        'grokked',
      ];
      return state
        .setIn(
          pathGenerators.grokkingItemSavingByQuestionId(
            action.payload.questionId
          ),
          SAVING_STATUS.ERROR
        )
        .setIn(path, action.payload.currentGrokValue);

    case actions.SUBMIT_QUESTION_MARK_REQUEST:
      path = [
        ...pathGenerators.responsesByQuestionId(action.payload.questionId),
        'text_marks_from_student',
      ];
      return state
        .setIn(path, action.payload.mark)
        .setIn(
          pathGenerators.markingItemSavingByQuestionId(
            action.payload.questionId
          ),
          SAVING_STATUS.SAVING
        );

    case actions.SUBMIT_QUESTION_MARK_SUCCESS:
      return state
        .setIn(
          pathGenerators.markingItemSavingByQuestionId(
            action.payload.questionId
          ),
          SAVING_STATUS.SAVED
        )
        .mergeDeep(action.payload.responseData.entities);

    case actions.SUBMIT_QUESTION_MARK_FAILURE:
      path = [
        ...pathGenerators.responsesByQuestionId(action.payload.questionId),
        'text_marks_from_student',
      ];
      return state
        .setIn(
          pathGenerators.markingItemSavingByQuestionId(
            action.payload.questionId
          ),
          SAVING_STATUS.ERROR
        )
        .setIn(path, action.payload.currentMarkValue);

    case actions.SUBMIT_CHECKLIST_REFLECTION_REQUEST: {
      const path = pathGenerators.checklistReflectionsByQuestionId(
        action.payload.questionId
      );
      const checked = action.payload.checklistReflectionItem.checked;
      const targetId =
        action.payload.checklistReflectionItem.exemplar_response_checklist_item;
      const existingReflections = state.getIn(path, Immutable.List());

      return state
        .updateIn(
          // todo: err, there's no way this is working
          path,
          existingReflections.findIndex(
            (item) => item.get('exemplar_response_checklist_item') == targetId
          ),
          (item) => item.set('checked', checked)
        )
        .setIn(
          pathGenerators.checklistItemSavingByQuestionId(
            action.payload.questionId
          ),
          SAVING_STATUS.SAVING
        );
    }

    case actions.SUBMIT_CHECKLIST_REFLECTION_SUCCESS:
      return state
        .setIn(
          pathGenerators.checklistItemSavingByQuestionId(
            action.payload.questionId
          ),
          SAVING_STATUS.SAVED
        )
        .mergeDeep(action.payload.responseData.entities);

    case actions.SUBMIT_CHECKLIST_REFLECTION_FAILURE: {
      const path = pathGenerators.checklistReflectionsByQuestionId(
        action.payload.questionId
      );
      const currentChecked =
        action.payload.checklistReflectionItem.currentChecked;
      const targetId =
        action.payload.checklistReflectionItem.exemplar_response_checklist_item;
      const existingReflections = state.getIn(path, Immutable.List());

      return state
        .setIn(
          pathGenerators.checklistItemSavingByQuestionId(
            action.payload.questionId
          ),
          SAVING_STATUS.ERROR
        )
        .updateIn(
          path,
          existingReflections.findIndex(
            (item) => item.get('exemplar_response_checklist_item') == targetId
          ),
          (item) => item.set('checked', currentChecked)
        );
    }

    case actions.FETCH_CLASS_LESSON_QUESTION_AGGREGATES_REQUEST:
      return state.set(
        reducerKeys.loadingText,
        'Loading Class Question Progress'
      );

    case actions.FETCH_CLASS_LESSON_QUESTION_AGGREGATES_SUCCESS: {
      const { examId, classId, aggregateData } = action.payload;
      return state
        .set(reducerKeys.loadingText, null)
        .setIn(
          [reducerKeys.classQuestionSetAggregates, `${examId}`, `${classId}`],
          Immutable.fromJS(aggregateData)
        );
    }

    case actions.LOAD_CLASS_EXAM_SITTINGS_REQUEST: {
      const currentStatus = state.getIn([
        reducerKeys.classExamSittingsStatus,
        `${action.payload.classId}-${action.payload.examId}`,
      ]);
      return state.setIn(
        [
          reducerKeys.classExamSittingsStatus,
          `${action.payload.classId}-${action.payload.examId}`,
        ],
        currentStatus === STATUS.READY || currentStatus === STATUS.RELOADING
          ? STATUS.RELOADING
          : STATUS.LOADING
      );
    }

    case actions.LOAD_CLASS_EXAM_SITTINGS_SUCCESS: {
      const { examId, classId, sittings } = action.payload;
      return state
        .setIn(
          [
            reducerKeys.classExamSittingsStatus,
            `${action.payload.classId}-${action.payload.examId}`,
          ],
          STATUS.READY
        )
        .setIn(
          [reducerKeys.classExamSittings, `${examId}`, `${classId}`],
          Immutable.fromJS(sittings)
        );
    }

    case actions.LOAD_CLASS_EXAM_SITTINGS_FAILURE:
      return state.setIn(
        [
          reducerKeys.classExamSittingsStatus,
          `${action.payload.classId}-${action.payload.examId}`,
        ],
        STATUS.FAILURE
      );

    case actions.FETCH_CLASS_LESSON_QUESTION_AGGREGATES_FAILURE:
      // yeah we should do errors.
      return state.set(reducerKeys.loadingText, null);

    default:
      return state;
  }
};
